import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShip,
  faHotel,
  faHome,
  faCheck,
  faMagic,
  faArrowRight,
  faCalendar,
  faGift,
} from '@fortawesome/free-solid-svg-icons';
import type { ILandingPageRenderData, ILandingPageRenderProduct } from '@share/common-types';
import { LANDING_DEFAULT_HERO_IMAGE, LANDING_DEFAULT_PRODUCT_IMAGES } from './landing-page-default-assets';
import { allocateRetailPrices } from './allocate-retail-prices';
import { ExperiencesGridSection } from './experiences-grid-section';
import { TestimonialsSection } from './testimonials-section';
import { CruiseLinesSection } from './cruise-lines-section';
import styles from './layout-showcase.module.scss';

interface LayoutShowcaseProps {
  data: ILandingPageRenderData;
}

interface ProductIconProps {
  type: string;
  className?: string;
}

const ZERO_SAVINGS = 0;
const PERCENT_FACTOR = 100;
const FALLBACK_PRODUCT_TYPE = 'hotel';

function ProductIcon({ type, className }: ProductIconProps): JSX.Element {
  const cls = className ?? styles.iconLg;
  if (type.toLowerCase() === 'cruise') {
    return <FontAwesomeIcon icon={faShip} className={cls} />;
  }
  if (type.toLowerCase() === 'hotel') {
    return <FontAwesomeIcon icon={faHotel} className={cls} />;
  }
  return <FontAwesomeIcon icon={faHome} className={cls} />;
}

export function LayoutShowcase({ data }: LayoutShowcaseProps): JSX.Element {
  const firstProduct = data.products[0]?.type?.toLowerCase() ?? FALLBACK_PRODUCT_TYPE;
  const [activeProduct, setActiveProduct] = useState<string>(firstProduct);

  const title = data.customHeadline ?? data.templateName;
  const tagline = data.customSubheadline ?? '';
  const ctaText = data.customCtaText ?? 'Buy Certificate';
  const heroImage = data.templateImageUrl ?? LANDING_DEFAULT_HERO_IMAGE;
  const savingsPercent = data.averageRetailPrice > ZERO_SAVINGS
    ? Math.round((data.savings / data.averageRetailPrice) * PERCENT_FACTOR)
    : ZERO_SAVINGS;

  const productImages: Record<string, string> = LANDING_DEFAULT_PRODUCT_IMAGES;

  const currentProduct: ILandingPageRenderProduct | undefined = data.products.find(
    (product) => product.type.toLowerCase() === activeProduct,
  );
  const retailLinePrices = allocateRetailPrices(data.products, data.averageRetailPrice);
  // Parent CertificateLandingPage sets customCtaUrl to paymentPath
  const ctaHref = data.customCtaUrl ?? '';
  const totalNights = data.products.reduce((sum, product) => sum + product.nights, ZERO_SAVINGS);

  const inclusionFeatures = [
    'Premium accommodations',
    'Access to resort facilities',
    'Complimentary Wi-Fi',
    'Daily housekeeping',
    'Concierge services',
    '24/7 support',
  ];

  return (
    <div className={styles.root}>
      <section className={styles.hero}>
        <div className={styles.heroMedia}>
          <img src={heroImage} alt="Vacation" />
          <div className={styles.heroOverlay} />
        </div>
        <div className={`container ${styles.heroInner}`}>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-4 mb-4">
            <div>
              {data.savings > ZERO_SAVINGS && (
                <span className={styles.savingsBadge}>
                  <FontAwesomeIcon icon={faGift} className={styles.iconSm} />
                  {' '}
                  Save $
                  {data.savings.toLocaleString()}
                </span>
              )}
              <h1 className={styles.heroTitle}>{title}</h1>
              {tagline && <p className={styles.heroTagline}>{tagline}</p>}
            </div>
            <div className="text-end">
              {data.averageRetailPrice > ZERO_SAVINGS && (
                <p className={styles.heroRetailStrike}>
                  $
                  {data.averageRetailPrice.toLocaleString()}
                </p>
              )}
              <p className={styles.heroPrice}>
                $
                {data.totalPrice.toLocaleString()}
              </p>
              <p className={styles.heroMeta}>
                <FontAwesomeIcon icon={faCalendar} className={styles.iconSm} />
                {' '}
                Valid
                {' '}
                {data.validityMonths}
                {' '}
                months
              </p>
            </div>
          </div>

          <div className="row g-4">
            {data.products.map((product) => {
              const isActive = activeProduct === product.type.toLowerCase();
              return (
                <div key={product.type} className="col-12 col-sm-4">
                  <button
                    type="button"
                    onClick={() => setActiveProduct(product.type.toLowerCase())}
                    className={`${styles.productTab} ${
                      isActive ? styles.productTabActive : styles.productTabInactive
                    }`}
                  >
                    <div
                      className={`${styles.productTabIcon} ${
                        isActive ? styles.productTabIconActive : ''
                      }`}
                    >
                      <ProductIcon type={product.type} className={styles.iconLg} />
                    </div>
                    <div>
                      <p className={styles.productTabTitle}>{product.type}</p>
                      <p className={styles.productTabMeta}>
                        {product.nights}
                        {' '}
                        nights
                      </p>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {currentProduct && (
        <section className={styles.showcaseSection}>
          <div className="container">
            <div className="row g-5">
              <div className="col-lg-6">
                <div className={styles.mediaSticky}>
                  <div className={styles.mediaFrame}>
                    <img
                      src={productImages[currentProduct.type.toLowerCase()] ?? heroImage}
                      alt={currentProduct.type}
                    />
                    <div className={styles.mediaOverlay} />
                    <span className={styles.includedChip}>
                      <FontAwesomeIcon icon={faMagic} className={styles.iconSm} />
                      {' '}
                      Included in package
                    </span>
                  </div>
                  <div className="row g-2 mt-3">
                    {data.products.map((product) => (
                      <div key={product.type} className="col-4">
                        <button
                          type="button"
                          onClick={() => setActiveProduct(product.type.toLowerCase())}
                          className={`${styles.thumb} ${
                            activeProduct === product.type.toLowerCase() ? styles.thumbActive : ''
                          }`}
                        >
                          <img
                            src={productImages[product.type.toLowerCase()] ?? heroImage}
                            alt={product.type}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <span className={styles.valueChip}>
                  Value: $
                  {currentProduct.price.toLocaleString()}
                </span>
                <h2 className={styles.sectionTitle}>
                  {currentProduct.nights}
                  -Night
                  {' '}
                  {currentProduct.type}
                  {' '}
                  Experience
                </h2>
                {data.customDescription ? (
                  <div
                    className={`lp-richtext lp-richtext--muted ${styles.sectionSubcopy}`}
                    dangerouslySetInnerHTML={{ __html: data.customDescription }}
                  />
                ) : (
                  <p className={styles.sectionSubcopy}>
                    Premium
                    {' '}
                    {currentProduct.type.toLowerCase()}
                    {' '}
                    accommodations with world-class amenities
                    and services included in your certificate.
                  </p>
                )}

                <div className="mb-4">
                  <h3 className={styles.inclusionsHeading}>What&apos;s Included:</h3>
                  <div className={styles.inclusionList}>
                    {inclusionFeatures.map((feature) => (
                      <div key={feature} className={styles.inclusionRow}>
                        <div className={styles.inclusionIcon}>
                          <FontAwesomeIcon icon={faCheck} className={styles.iconSm} />
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.priceCard}>
                  <div className="mb-3">
                    <p className={styles.priceCardLabel}>Total Package Price</p>
                    <div className="d-flex align-items-center justify-content-center gap-3">
                      {data.averageRetailPrice > ZERO_SAVINGS && (
                        <span className={styles.priceCardRetail}>
                          $
                          {data.averageRetailPrice.toLocaleString()}
                        </span>
                      )}
                      <span className={styles.priceCardTotal}>
                        $
                        {data.totalPrice.toLocaleString()}
                      </span>
                    </div>
                    {data.savings > ZERO_SAVINGS && (
                      <span className={`${styles.savingsBadge} mt-2`}>
                        Save $
                        {data.savings.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <a href={ctaHref} className={styles.ctaButton}>
                    {ctaText}
                    <FontAwesomeIcon icon={faArrowRight} className={styles.iconMd} />
                  </a>
                  <p className={styles.priceCardFootnote}>
                    All
                    {' '}
                    {data.products.length}
                    {' '}
                    vacation packages included
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <ExperiencesGridSection data={data} sectionClassName="bg-white" />

      {data.customItems && data.customItems.length > 0 && (
        <section className={styles.featuresSection}>
          <div className="container">
            <div className={styles.featuresHeader}>
              <span className={styles.featuresEyebrow}>
                <FontAwesomeIcon icon={faMagic} className={styles.iconXs} />
                {' '}
                Certificate Features
              </span>
              <h2 className={styles.sectionTitle}>Highlight your offer</h2>
              <p className={styles.featuresSubcopy}>
                Custom callouts and selling points for this landing page.
              </p>
            </div>

            <div className="row g-4">
              {data.customItems.map((item, index) => (
                <div key={index} className="col-md-6 col-lg-4">
                  <div className={styles.featureCard}>
                    {item.imageUrl && (
                      <div className={styles.featureCardMedia}>
                        <img src={item.imageUrl} alt={item.title} />
                      </div>
                    )}
                    <div className={styles.featureCardBody}>
                      <h3 className={styles.featureCardTitle}>{item.title}</h3>
                      {item.description && (
                        <p className={styles.featureCardCopy}>{item.description}</p>
                      )}
                      {item.subtitle && !item.description && (
                        <p className={styles.featureCardCopy}>{item.subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.products.length > 0 && data.averageRetailPrice > ZERO_SAVINGS && (
        <section className={styles.compareSection}>
          <div className="container">
            <div className={styles.compareWrap}>
              <div className={styles.compareHeader}>
                <h2 className={styles.sectionTitle}>The Smart Way to Vacation</h2>
                <p className={styles.compareSubcopy}>
                  See how much you save with our bundled certificate
                </p>
              </div>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className={`${styles.compareCard} h-100`}>
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className={styles.compareIconWell}>
                        <FontAwesomeIcon icon={faCalendar} className={styles.iconMd} />
                      </div>
                      <div>
                        <p className="mb-0 fw-semibold">Booking Separately</p>
                        <p className="mb-0 small text-secondary">Standard retail prices</p>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {data.products.map((product, index) => (
                        <div key={product.type} className={styles.compareLine}>
                          <div className="d-flex align-items-center gap-2">
                            <ProductIcon type={product.type} className={styles.iconSm} />
                            <span className="text-capitalize">
                              {product.type}
                              {' '}
                              (
                              {product.nights}
                              {' '}
                              nights)
                            </span>
                          </div>
                          <span className="fw-semibold">
                            $
                            {retailLinePrices[index].toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.compareFooter}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-secondary">Retail Total</span>
                        <span className={styles.compareRetailTotal}>
                          $
                          {data.averageRetailPrice.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className={`${styles.compareCardFeatured} h-100`}>
                    <span className={styles.bestValueBadge}>Best Value</span>
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className={styles.compareIconWellFeatured}>
                        <FontAwesomeIcon icon={faGift} className={styles.iconMd} />
                      </div>
                      <div>
                        <p className="mb-0 fw-semibold">With Certificate</p>
                        <p className="mb-0 small text-secondary">All-inclusive bundle</p>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {data.products.map((product) => (
                        <div key={product.type} className={styles.compareLineIncluded}>
                          <FontAwesomeIcon icon={faCheck} className={styles.iconMd} />
                          <span className="text-capitalize">
                            {product.nights}
                            -Night
                            {' '}
                            {product.type}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className={styles.compareFooterFeatured}>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className={`${styles.stickyRetailStrike} d-block`}>
                            $
                            {data.averageRetailPrice.toLocaleString()}
                          </span>
                          <span className="fw-medium">Your Price</span>
                        </div>
                        <span className={styles.compareYourPrice}>
                          $
                          {data.totalPrice.toLocaleString()}
                        </span>
                      </div>
                      {data.savings > ZERO_SAVINGS && (
                        <div className={styles.saveBanner}>
                          You save $
                          {data.savings.toLocaleString()}
                          {' '}
                          (
                          {savingsPercent}
                          % off)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <CruiseLinesSection data={data} />
      <TestimonialsSection data={data} />

      <div className={styles.stickyCta}>
        <div className="container d-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className={styles.avatarStack}>
              {data.products.map((product) => (
                <div key={product.type} className={styles.avatarWell}>
                  <ProductIcon type={product.type} className={styles.iconSm} />
                </div>
              ))}
            </div>
            <div>
              <p className={styles.stickySummaryTitle}>
                {totalNights}
                {' '}
                Nights Total
              </p>
              <p className={styles.stickySummaryMeta}>
                {data.products.length}
                {' '}
                vacations included
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="text-end">
              {data.averageRetailPrice > ZERO_SAVINGS && (
                <p className={styles.stickyRetailStrike}>
                  $
                  {data.averageRetailPrice.toLocaleString()}
                </p>
              )}
              <p className={styles.stickyPrice}>
                $
                {data.totalPrice.toLocaleString()}
              </p>
            </div>
            <a href={ctaHref} className={styles.stickyCtaButton}>
              {ctaText}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
